import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { FaSave } from "react-icons/fa";
import "../css/others/Table.css";
import { apiCall } from "../main/ApiCall";
import ToastPopup from "../popups/ToastPopup";
import FieldPopup from "../popups/FieldPopup";
import { TypeEnum, NCQuestion, createToastMessage, ToastMessage } from "../interFaces/interfaces";
import EncryptionUtils from "../main/encryptionUtils";
interface ChecklistDetails { content: NCQuestion[]; totalElements: number; totalPages: number }
const NCTrackerDetails: React.FC = () => {
  const { campaignName } = useParams();
  const location = useLocation();
  const { auditId, campaignId, projectId } = location.state || {};
  const [details, setDetails] = useState<ChecklistDetails>({ content: [], totalElements: 0, totalPages: 0, });
  const [, setLoading] = useState<boolean>(true);
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [ncTrackerEnums, setNcTrackerEnums] = useState<TypeEnum[]>([]);
  const [typeEnums, setTypeEnums] = useState<TypeEnum[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState<{ message: string; onConfirm: () => void; onCancel?: () => void; }>({ message: "", onConfirm: () => { } });
  const [fieldPopup, setFieldPopup] = useState<{ isOpen: boolean; fieldName: string; fieldValue: string; rowIndex: number; fieldKey: keyof NCQuestion }>({ isOpen: false, fieldName: "", fieldValue: "", rowIndex: -1, fieldKey: "correctiveAction" });
  const [controlColumns, setControlColumns] = useState<string[]>([]);
  const [showPracticeAreaColumn, setShowPracticeAreaColumn] = useState<boolean>(true);
  const userRole = EncryptionUtils.get("roleName");
  const email = localStorage.getItem("email");
  const isEditableUser = ["Quality Consultant", "Project Manager"].includes(userRole);
  const restrictedCharsRegex = /[\\:*?"<>|/]/;
  const controlHeaders = [{ key: "pims", label: "PIMS" }, { key: "isms", label: "ISMS" }, { key: "bcms", label: "BCMS" }, { key: "cmmi", label: "CMMI" }, { key: "soc1", label: "SOC1" }, { key: "soc2", label: "SOC2" }, { key: "qms", label: "QMS" }, { key: "aims", label: "AIMS" }, { key: "erm", label: "ERM" }, { key: "itsm", label: "ITSM" }, { key: "ers", label: "ERS" }, { key: "ohs", label: "OHS" }] as const;
  type ControlKey = typeof controlHeaders[number]["key"];
  const truncateWords = (text: string, wordLimit: number = 10) => {
    if (!text) return "";
    const words = text.trim().split(/\s+/);
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(" ") + " ...";
  };
  const validateNoRestrictedChars = (value: string) => {
    return !restrictedCharsRegex.test(value);
  };
  const isDisabled = useCallback(
    (question: NCQuestion) => {
      return (
        !isEditableUser || (question.isSaved && (question.ncTrackerStatus === "CLOSED" || question.ncTrackerStatus === "DEFERRED"))
      );
    },
    [isEditableUser]
  );
  useEffect(() => {
    const fetchNcTrackerEnums = async () => {
      try {
        const response = await apiCall("get", "/api/enums/NCTrackerEnum");
        setNcTrackerEnums(response);
      } catch (error) {
        console.error("Error fetching NcTrackerEnum values:", error);
      }
    };
    fetchNcTrackerEnums();
  }, []);
  useEffect(() => {
    const fetchTypeEnums = async () => {
      try {
        const response = await apiCall("get", "/api/enums/TypeEnum");
        setTypeEnums(response);
      } catch (error) {
        console.error("Error fetching TypeEnum values:", error);
      }
    };
    fetchTypeEnums();
  }, []);
  const showConfirmModal = (message: string, onConfirm: () => void, onCancel?: () => void) => {
    setConfirmModalConfig({ message, onConfirm, onCancel });
    setIsConfirmModalOpen(true);
  };
  const fetchChecklistDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiCall("get", `/api/nctracker/campaign/${campaignId}/project/${projectId}/audit/${auditId}/questions`, null, { page: currentPage - 1, size: itemsPerPage });
      if (!response || !response.content) {
        setDetails({ content: [], totalElements: 0, totalPages: 0 });
        return;
      }
      const availableControls: string[] = [];
      const controlFields = ["pims", "isms", "bcms", "cmmi", "soc1", "soc2", "qms", "aims", "erm", "itsm", "ers", "ohs"];
      controlFields.forEach((field) => {
        if (response.content.some((item: any) => item[field])) {
          availableControls.push(field);
        }
      });
      setControlColumns(availableControls);
      const hasControlFields = response.content.some((item: any) => item.pims || item.isms || item.bcms || item.cmmi || item.soc1 || item.soc2 || item.qms || item.aims || item.erm || item.itsm || item.ers || item.ohs);
      setShowPracticeAreaColumn(!hasControlFields);
      const questionsWithSavedFlag = response.content.map((question: any) => ({ ...question, isSaved: true }));
      setDetails({ ...response, content: questionsWithSavedFlag });
    } catch (error) {
      console.error("Error fetching checklist details:", error);
      setDetails({ content: [], totalElements: 0, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  }, [campaignId, projectId, auditId, currentPage, itemsPerPage]);
  useEffect(() => { fetchChecklistDetails() }, [fetchChecklistDetails]);
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const selectableQuestions = details.content.filter((item) => !isDisabled(item));
      const isIndeterminate =
        selectedQuestions.length > 0 && selectedQuestions.length < selectableQuestions.length;
      selectAllCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [selectedQuestions, details.content, isDisabled]);
  const handlePageChange = (newPage: number) => {
    if (hasUnsavedChanges) {
      showConfirmModal("You have unsaved changes. Are you sure you want to go to the next page?", () => {
        setCurrentPage(newPage);
        setHasUnsavedChanges(false);
      });
      return;
    }
    setCurrentPage(newPage);
  };
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = "You have unsaved changes. Do you want to leave?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);
  const handleInputChange = (index: number, key: keyof NCQuestion, value: any) => {
    setDetails((prevDetails) => {
      const updatedContent = [...prevDetails.content];
      if (key === "verifiedByQC") {
        updatedContent[index] = { ...updatedContent[index], [key]: value === "true" || value === true, isSaved: false };
      } else {
        updatedContent[index] = { ...updatedContent[index], [key]: value, isSaved: false };
      }
      return {
        ...prevDetails, content: updatedContent,
      };
    });
    setHasUnsavedChanges(true);
  };
  const handleNavigation = () => {
    if (hasUnsavedChanges) {
      showConfirmModal("You have unsaved changes. Are you sure you want to leave?", () =>
        window.history.back()
      );
    } else {
      window.history.back();
    }
  };
  const validateRequiredFields = (question: NCQuestion) => {
    const missingFields: string[] = [];
    if (!question.correctiveAction) missingFields.push("Corrective Action");
    if (!question.correction) missingFields.push("Correction");
    if (!question.rca) missingFields.push("RCA");
    if (!question.actionTaken) missingFields.push("Action Taken");
    if (question.verifiedByQC === undefined) missingFields.push("Verified By QC (must select Yes or No)");
    if (!question.qcComments) missingFields.push("QC Comments");
    if (!question.ncTrackerStatus) missingFields.push("NC Status");
    return {
      isValid: missingFields.length === 0,
      missingFields,
    };
  };
  const handleNCTrackerSave = async (questionId: string) => {
    const questionToSave = details.content.find((item) => item.questionId === questionId);
    if (!questionToSave) {
      setMessage(createToastMessage("Question not found.", "warning", 3000));
      return;
    }
    const validation = validateRequiredFields(questionToSave);
    if (!validation.isValid) {
      setMessage(createToastMessage(`Please fill the following required fields: ${validation.missingFields.join(", ")}`, "warning", 3000));
      return;
    }
    const NCTrackerId = questionToSave.id;
    if (!NCTrackerId) {
      setMessage(createToastMessage("Invalid NC Tracker ID.", "error", 3000));
      return;
    }
    const payload = { id: NCTrackerId, questionId: questionToSave.questionId, correctiveAction: questionToSave.correctiveAction, correction: questionToSave.correction, ncTrackerStatus: questionToSave.ncTrackerStatus, rca: questionToSave.rca, actionTaken: questionToSave.actionTaken, verifiedByQC: questionToSave.verifiedByQC, qcComments: questionToSave.qcComments, pims: questionToSave.pims, isms: questionToSave.isms, bcms: questionToSave.bcms, cmmi: questionToSave.cmmi, soc1: questionToSave.soc1, soc2: questionToSave.soc2, qms: questionToSave.qms, aims: questionToSave.aims, erm: questionToSave.erm, itsm: questionToSave.itsm, ers: questionToSave.ers, ohs: questionToSave.ohs, updatedBy: email };
    try {
      await apiCall("put", `/api/nctracker/${NCTrackerId}`, payload);
      setDetails((prevDetails) => {
        const updatedContent = prevDetails.content.map((item) => item.questionId === questionId ? { ...item, isSaved: true } : item);
        return { ...prevDetails, content: updatedContent };
      });
      setMessage(createToastMessage("Question saved successfully!", "success", 3000));
      setHasUnsavedChanges(false);
    } catch (error: any) {
      setMessage(createToastMessage(error?.response?.data?.message || "Error saving question", "error", 3000));
    }
  };
  const handleSelectAll = () => {
    const selectableQuestions = details.content.filter((item) => !isDisabled(item));
    if (selectedQuestions.length === selectableQuestions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(selectableQuestions.map((item) => item.questionId));
    }
  };
  const handleNCTrackerSaveAll = async () => {
    if (selectedQuestions.length === 0) {
      setMessage(createToastMessage("Please select at least one question to save.", "warning", 3000));
      return;
    }
    const invalidQuestions: { id: string; missingFields: string[] }[] = [];
    selectedQuestions.forEach((questionId) => {
      const question = details.content.find((q) => q.questionId === questionId);
      if (question) {
        const validation = validateRequiredFields(question);
        if (!validation.isValid) {
          invalidQuestions.push({ id: question.questionId, missingFields: validation.missingFields });
        }
      }
    });
    if (invalidQuestions.length > 0) {
      const errorMessage = `Please fill all required fields for the following questions:\n${invalidQuestions
        .map((q) => `Question ${q.id}: ${q.missingFields.join(", ")}`)
        .join("\n")}`;
      setMessage(createToastMessage(`Please fill all required fields:\n${errorMessage}`, "warning", 3000));
      return;
    }
    const payloads = selectedQuestions
      .map((questionId) => {
        const question = details.content.find((q) => q.questionId === questionId);
        if (!question) return null;
        return {
          id: question.id, questionId: question.questionId, correctiveAction: question.correctiveAction, correction: question.correction, ncTrackerStatus: question.ncTrackerStatus, rca: question.rca, actionTaken: question.actionTaken, verifiedByQC: question.verifiedByQC, qcComments: question.qcComments, pims: question.pims, isms: question.isms, bcms: question.bcms, cmmi: question.cmmi, soc1: question.soc1, soc2: question.soc2, qms: question.qms, aims: question.aims, erm: question.erm, itsm: question.itsm, ers: question.ers, ohs: question.ohs, updatedBy: email
        };
      })
      .filter((payload): payload is NonNullable<typeof payload> => payload !== null);
    try {
      if (payloads.length === 1) {
        await apiCall("put", `/api/nctracker/${payloads[0].id}`, payloads[0]);
      } else {
        await apiCall("put", "/api/nctracker", payloads);
      }
      setDetails((prevDetails) => {
        const updatedContent = prevDetails.content.map((item) =>
          selectedQuestions.includes(item.questionId) ? { ...item, isSaved: true } : item
        );
        return { ...prevDetails, content: updatedContent };
      });
      setMessage(createToastMessage(`${selectedQuestions.length} question(s) saved successfully!`, "success", 1500));
      setHasUnsavedChanges(false);
      setSelectedQuestions([]);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Error saving questions.";
      createToastMessage(errorMessage, "error", 3000);
    }
  };
  const handleSelectQuestion = (questionId: string) => {
    setSelectedQuestions((prevSelected) =>
      prevSelected.includes(questionId) ? prevSelected.filter((id) => id !== questionId) : [...prevSelected, questionId]
    );
  };
  const getPageNumbers = (): number[] => {
    const total = details.totalPages;
    if (total <= 3) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    if (currentPage === 1) {
      return [1, 2, 3];
    } else if (currentPage === total) {
      return [total - 2, total - 1, total];
    } else {
      return [currentPage - 1, currentPage, currentPage + 1];
    }
  };
  const openFieldPopup = (rowIndex: number, fieldKey: keyof NCQuestion, fieldName: string, currentValue: string) => {
    if (!isEditableUser) return;
    setFieldPopup({ isOpen: true, fieldName, fieldValue: currentValue, rowIndex, fieldKey });
  };
  const handleFieldPopupSave = (value: string) => {
    if (!validateNoRestrictedChars(value)) {
      setMessage(createToastMessage(`Special characters are not allowed - ${restrictedCharsRegex}`, "warning", 3000));
      return;
    }
    if (fieldPopup.rowIndex !== -1) {
      handleInputChange(fieldPopup.rowIndex, fieldPopup.fieldKey, value);
    }
    setFieldPopup({ isOpen: false, fieldName: "", fieldValue: "", rowIndex: -1, fieldKey: "correctiveAction", });
  };
  const handleFieldPopupClose = () => {
    setFieldPopup({ isOpen: false, fieldName: "", fieldValue: "", rowIndex: -1, fieldKey: "correctiveAction" });
  };
  const renderFieldContent = (value: string | undefined, defaultValue: string = "-") => {
    return value ? value : defaultValue;
  };
  const getNoDataColSpan = () => {
    let baseCols = 11;
    if (isEditableUser) baseCols += 2;
    if (showPracticeAreaColumn) baseCols += 1;
    baseCols += controlColumns.length;
    return baseCols;
  };
  const handleOpenCorrectiveActionPopup = (index: number, item: any) => {
    openFieldPopup(index, "correctiveAction", "Corrective Action", item.correctiveAction || "");
  };
  const handleOpenCorrectionPopup = (index: number, item: any) => {
    openFieldPopup(index, "correction", "Correction", item.correction || "");
  };
  const handleOpenRCAPopup = (index: number, item: any) => {
    openFieldPopup(index, "rca", "RCA", item.rca || "");
  };
  const handleOpenActionTakenPopup = (index: number, item: any) => {
    openFieldPopup(index, "actionTaken", "Action Taken", item.actionTaken || "");
  };
  const handleOpenQCCommentsPopup = (index: number, item: any) => {
    openFieldPopup(index, "qcComments", "QC Comments", item.qcComments || "");
  };
  return (
    <div className="table-details">
      <div className="table-title">
        <h2>NC Tracker Details - {campaignName}</h2>
        <div className="table-title-actions">
          {isEditableUser && (
            <button className="save-all-button" onClick={handleNCTrackerSaveAll} disabled={selectedQuestions.length === 0} title={selectedQuestions.length === 0 ? "Please select at least one question to save" : ""}> Save All </button>
          )}
        </div>
      </div>
      <div className="table-container">
        <div className="table">
          <table className="main-table">
            <thead>
              <tr>
                {isEditableUser && (
                  <th>
                    <input type="checkbox" ref={selectAllCheckboxRef} onChange={handleSelectAll} checked={details.content.length > 0 && selectedQuestions.length === details.content.filter((item) => !isDisabled(item)).length} />
                  </th>
                )}
                <th>S.No.</th>
                <th>Question</th>
                {showPracticeAreaColumn && <th className="header-width">Control/Practice Area</th>}
                <th className="header-width">Type of Findings</th>
                <th className="header-width">Findings Description</th>
                {controlHeaders
                  .filter(col => controlColumns.includes(col.key))
                  .map(col => (
                    <th key={col.key} className="header-width">{col.label}</th>
                  ))}
                <th className="header-width">Corrective Action</th>
                <th className="header-width">Correction</th>
                <th className="header-width">RCA</th>
                <th className="header-width">Action Taken</th>
                <th>Verified By QC</th>
                <th className="header-width">QC Comments</th>
                <th className="nc-status-header">NC Status</th>
                {isEditableUser && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {details.content.length > 0 ? (
                details.content.map((item, index) => {
                  const disabled = isDisabled(item);
                  return (
                    <tr key={item.questionId}>
                      {isEditableUser && (
                        <td>
                          <input type="checkbox" checked={selectedQuestions.includes(item.questionId)} onChange={() => handleSelectQuestion(item.questionId)} disabled={disabled} />
                        </td>
                      )}
                      <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td className="question-text-column">{item.questionText}</td>
                      {showPracticeAreaColumn && <td>{item.practiceArea || "N/A"}</td>}
                      <td>
                        {renderFieldContent(typeEnums.find((e) => e.value === item.typeOfFindings)?.description || item.typeOfFindings, "N/A")}
                      </td>
                      <td>{renderFieldContent(item.findings, "N/A")}</td>
                      {controlHeaders
                        .filter(col => controlColumns.includes(col.key))
                        .map(col => {
                          const value = item[col.key as ControlKey];
                          return (
                            <td key={col.key}>{typeof value === "string" ? value : renderFieldContent(value as unknown as string, "N/A")}</td>
                          );
                        })}
                      <td>
                        {disabled ? (
                          <span>{renderFieldContent(item.correctiveAction, "N/A")}</span>
                        ) : isEditableUser ? (
                          <div className={`editable-field editable-text-column ${!item.correctiveAction ? "missing-field" : ""}`} onClick={() => handleOpenCorrectiveActionPopup(index, item)}>{item.correctiveAction ? truncateWords(item.correctiveAction, 10) : "Click to Enter Inputs"}</div>
                        ) : (
                          <span>{renderFieldContent(item.correctiveAction, "N/A")}</span>
                        )}
                      </td>
                      <td>
                        {disabled ? (
                          <span>{renderFieldContent(item.correction, "N/A")}</span>
                        ) : isEditableUser ? (
                          <div className={`editable-field editable-text-column ${!item.correction ? "missing-field" : ""}`} onClick={() => handleOpenCorrectionPopup(index, item)}>{item.correction ? truncateWords(item.correction, 10) : "Click to Enter Inputs"}</div>
                        ) : (
                          <span>{renderFieldContent(item.correction, "N/A")}</span>
                        )}
                      </td>
                      <td>
                        {disabled ? (
                          <span>{renderFieldContent(item.rca, "N/A")}</span>
                        ) : isEditableUser ? (
                          <div className={`editable-field editable-text-column ${!item.rca ? "missing-field" : ""}`} onClick={() => handleOpenRCAPopup(index, item)}>{item.rca ? truncateWords(item.rca, 10) : "Click to Enter Inputs"}</div>
                        ) : (
                          <span>{renderFieldContent(item.rca, "N/A")}</span>
                        )}
                      </td>
                      <td>
                        {disabled ? (
                          <span>{renderFieldContent(item.actionTaken, "N/A")}</span>
                        ) : isEditableUser ? (
                          <div className={`editable-field editable-text-column ${!item.actionTaken ? "missing-field" : ""}`} onClick={() => handleOpenActionTakenPopup(index, item)}>{item.actionTaken ? truncateWords(item.actionTaken, 10) : "Click to Enter Inputs"}</div>
                        ) : (
                          <span>{renderFieldContent(item.actionTaken, "N/A")}</span>
                        )}
                      </td>
                      <td className="nc-status-cell">
                        {disabled ? (
                          <span>{item.verifiedByQC ? "Yes" : "No"}</span>
                        ) : (
                          <select value={String(item.verifiedByQC)} onChange={(e) => handleInputChange(index, "verifiedByQC", e.target.value === "true")} className={item.verifiedByQC === undefined ? "missing-field" : ""}>
                            <option value="">Select Verification</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        )}
                      </td>
                      <td>
                        {disabled ? (
                          <span>{renderFieldContent(item.qcComments, "N/A")}</span>
                        ) : isEditableUser ? (
                          <div className={`editable-field editable-text-column ${!item.qcComments ? "missing-field" : ""}`} onClick={() => handleOpenQCCommentsPopup(index, item)}>{item.qcComments ? truncateWords(item.qcComments, 10) : "Click to Enter Inputs"}</div>
                        ) : (
                          <span>{renderFieldContent(item.qcComments, "N/A")}</span>
                        )}
                      </td>
                      <td className="nc-status-cell">
                        {disabled ? (
                          <span>
                            {renderFieldContent(item.ncTrackerStatus ? ncTrackerEnums.find((e) => e.value === item.ncTrackerStatus)?.description || item.ncTrackerStatus : undefined, "N/A")}
                          </span>
                        ) : isEditableUser ? (
                          <select value={item.ncTrackerStatus || ""} onChange={(e) => handleInputChange(index, "ncTrackerStatus", e.target.value)} className={!item.ncTrackerStatus ? "missing-field" : ""}>
                            <option value="">Select Status</option>
                            {ncTrackerEnums.map((enumItem) => (
                              <option key={enumItem.value} value={enumItem.value}> {enumItem.description}</option>
                            ))}
                          </select>
                        ) : (
                          <span>{renderFieldContent(item.ncTrackerStatus ? ncTrackerEnums.find((e) => e.value === item.ncTrackerStatus)?.description || item.ncTrackerStatus : undefined, "N/A")}</span>
                        )}
                      </td>
                      {isEditableUser && (
                        <td>
                          {disabled ? (
                            <span className="na-text">N/A</span>
                          ) : (
                            <FaSave className="action-button" onClick={() => handleNCTrackerSave(item.questionId)} title="Save" />
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={getNoDataColSpan()} className="no-data"> No questions available for this checklist</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="pagination">
        <button className="back-btn" onClick={handleNavigation}> Back</button>
        <button className="info"> Showing {(currentPage - 1) * itemsPerPage + 1} to{" "} {Math.min(currentPage * itemsPerPage, details.totalElements)} of {details.totalElements} items</button>
        <div className="pagination-controls">
          <button className="pagination-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1} > <span className="small-pipe">|</span>&#8249; </button>
          <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>&#8249; </button>
          {getPageNumbers().map((page) => (
            <button key={page} onClick={() => handlePageChange(page)} className={`pagination-btn ${currentPage === page ? "active" : ""}`}> {page}</button>
          ))}
          <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === details.totalPages}> &#8250;</button>
          <button className="pagination-btn" onClick={() => handlePageChange(details.totalPages)} disabled={currentPage === details.totalPages}>  &#8250;<span className="small-pipe">|</span></button>
        </div>
        <div>
          <select id="itemsPerPageSelect" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1) }} className="items-perpage-select">
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
      {isConfirmModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Confirmation</h2>
            <p>{confirmModalConfig.message}</p>
            <div className="buttons">
              <button className="cancelbutton" onClick={() => { confirmModalConfig.onCancel?.(); setIsConfirmModalOpen(false) }}>Cancel </button>
              <button className="savebutton" onClick={() => { confirmModalConfig.onConfirm(); setIsConfirmModalOpen(false); }} > Confirm</button>
            </div>
          </div>
        </div>
      )}
      {fieldPopup.isOpen && (
        <FieldPopup isOpen={fieldPopup.isOpen} fieldName={fieldPopup.fieldName} fieldValue={fieldPopup.fieldValue} rowIndex={fieldPopup.rowIndex} onSave={handleFieldPopupSave} onClose={handleFieldPopupClose} />
      )}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default NCTrackerDetails;